import { Basket } from '@reward-platform/order-schemas/basket'
import { CancelledError, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'
import { useAuthContext } from '~/context/auth'
import { useCallback, useEffect, useMemo } from 'react'
import { useUser } from '~/context/user'
import { useFeatureFlag } from '~/context/featureFlag'
import {
  addCarHireToBasket,
  addExperienceToBasket,
  addFlightToBasket,
  addHotelToBasket,
  clearItemsFromBasket,
  getBasket,
  removeItemFromBasket,
  validateBasketForCheckout,
} from '~/services/basketService'
import type { BasketRequestContext } from '~/services/basketService'
import { useNotification } from '~/context/notification'
import { GenericClientError } from '~/utils/errors'
import {
  beginCheckoutValidation,
  cancelCheckoutValidation as cancelValidation,
  CHECKOUT_BASKET_KEY,
  CheckoutBasket,
  CHECKOUT_REDEEMABILITY_GATE_FLAG,
  resetCheckoutBasket as resetCheckoutBasketState,
  useCheckoutBasket,
} from './useCheckoutBasket'

export const BASKET_KEY = 'basket'

type UseBasketReturnType = {
  basket: UseQueryResult<Basket>
  addCarHire: typeof addCarHireToBasket
  addHotel: typeof addHotelToBasket
  addExperience: typeof addExperienceToBasket
  addFlight: typeof addFlightToBasket
  removeItem: typeof removeItemFromBasket
  clearItems: typeof clearItemsFromBasket
  validateForCheckout: () => Promise<Basket>
  resetCheckoutBasket: VoidFunction
  cancelCheckoutValidation: VoidFunction
}

type UseBasketParams = {
  isEnabled?: boolean
  requestContext?: BasketRequestContext
}

export const useBasketQuery = ({ isEnabled = true, requestContext }: UseBasketParams) => {
  const queryClient = useQueryClient()
  const isRedeemabilityGateEnabled = useFeatureFlag(CHECKOUT_REDEEMABILITY_GATE_FLAG)
  const checkoutBasket = useCheckoutBasket()
  const { isAuthenticated } = useAuthContext()
  const { user } = useUser()
  const { addErrorCodeNotification } = useNotification()

  useEffect(() => {
    if (!isRedeemabilityGateEnabled) {
      cancelValidation(queryClient)
      if (checkoutBasket) {
        resetCheckoutBasketState(queryClient)
        queryClient.invalidateQueries({ queryKey: [BASKET_KEY], exact: true })
      }
    }
  }, [checkoutBasket, isRedeemabilityGateEnabled, queryClient])

  const basket = useQuery({
    queryKey: [BASKET_KEY],
    queryFn: () => {
      const cachedCheckoutBasket = isRedeemabilityGateEnabled
        ? queryClient.getQueryData<CheckoutBasket>(CHECKOUT_BASKET_KEY)?.basket
        : undefined
      return cachedCheckoutBasket ?? getBasket(false, requestContext)
    },
    staleTime: 0,
    refetchOnWindowFocus: () =>
      !isRedeemabilityGateEnabled || !queryClient.getQueryData(CHECKOUT_BASKET_KEY),
    refetchOnReconnect: () =>
      !isRedeemabilityGateEnabled || !queryClient.getQueryData(CHECKOUT_BASKET_KEY),
    refetchOnMount: false,
    enabled: isEnabled && !!isAuthenticated && !!user,
  })

  const { error, isError } = basket

  useEffect(() => {
    if (isError && error instanceof GenericClientError) {
      addErrorCodeNotification('ORDERS_BASKET_ERROR', {
        type: 'ERROR',
        errorCode: error.code,
        statusCode: error.httpStatus,
      })
    }
  }, [isError, error, addErrorCodeNotification])

  return basket
}

const useBasket = ({ isEnabled }: UseBasketParams = {}): UseBasketReturnType => {
  const queryClient = useQueryClient()
  const resetCheckoutBasket = useCallback(() => {
    resetCheckoutBasketState(queryClient)
  }, [queryClient])
  const cancelCheckoutValidation = useCallback(() => {
    cancelValidation(queryClient)
  }, [queryClient])
  const { userCountry, userCurrency } = useUser()
  const requestContext = useMemo<BasketRequestContext>(
    () => ({ countryCode: userCountry, currencyCode: userCurrency }),
    [userCountry, userCurrency]
  )

  const invalidateBasketQuery = useCallback(() => {
    resetCheckoutBasket()
    return queryClient.invalidateQueries({
      queryKey: [BASKET_KEY],
    })
  }, [queryClient, resetCheckoutBasket])

  const clearItems = useCallback(async () => {
    await clearItemsFromBasket(requestContext)
    await invalidateBasketQuery()
  }, [invalidateBasketQuery, requestContext])

  const basket = useBasketQuery({ isEnabled, requestContext })

  const validateForCheckout = useCallback(async () => {
    const attempt = beginCheckoutValidation(queryClient, requestContext)
    const throwIfValidationCancelled = () => {
      if (attempt.signal.aborted) {
        throw new CancelledError({ silent: true })
      }
    }

    try {
      await queryClient.cancelQueries({ queryKey: [BASKET_KEY], exact: true })
      throwIfValidationCancelled()

      const validatedBasket = await validateBasketForCheckout(requestContext, attempt.signal)
      throwIfValidationCancelled()

      // Reads started during validation must not overwrite the accepted response.
      await queryClient.cancelQueries({ queryKey: [BASKET_KEY], exact: true })
      throwIfValidationCancelled()

      queryClient.setQueryData<CheckoutBasket>(CHECKOUT_BASKET_KEY, {
        basket: validatedBasket,
        requestContext,
      })
      queryClient.setQueryData([BASKET_KEY], validatedBasket)
      return validatedBasket
    } catch (error) {
      throwIfValidationCancelled()
      throw error
    } finally {
      attempt.finish()
    }
  }, [queryClient, requestContext])

  return useMemo(
    () => ({
      basket,
      addCarHire: async (offer) => {
        await addCarHireToBasket(offer, requestContext)
        await invalidateBasketQuery()
      },
      addHotel: async (offer) => {
        await addHotelToBasket(offer, requestContext)
        await invalidateBasketQuery()
      },
      addExperience: async (offer) => {
        await addExperienceToBasket(offer, requestContext)
        await invalidateBasketQuery()
      },
      addFlight: async (offer) => {
        await addFlightToBasket(offer, requestContext)
        await invalidateBasketQuery()
      },
      clearItems,
      validateForCheckout,
      resetCheckoutBasket,
      cancelCheckoutValidation,
      removeItem: async (recommendationId: string) => {
        // A pending validation must not restore an item removed from the basket.
        cancelCheckoutValidation()
        await removeItemFromBasket(recommendationId, requestContext)
        await invalidateBasketQuery()
      },
    }),
    [
      basket,
      cancelCheckoutValidation,
      clearItems,
      invalidateBasketQuery,
      requestContext,
      resetCheckoutBasket,
      validateForCheckout,
    ]
  )
}

export const useBasketItemsCount = (): number => {
  const {
    basket: { data: basket },
  } = useBasket()
  const items = basket?.items

  if (items) {
    return Object.values(items).reduce((sum, itemCategory) => sum + itemCategory.length, 0)
  }

  return 0
}

export default useBasket
